import { Injectable } from '@angular/core';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, Observable, Subject, Subscription, tap } from 'rxjs';

import { IUser, UsersService } from '@app/core/users';
import { ApplicationsService, IApplication } from '../applications';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUser: IUser;
  private keycloakEventSubscription: Subscription | null = null;
  private tokenCheckTimer: any = null;

  // Key para compartir última actividad entre pestañas
  private readonly LAST_ACTIVITY_KEY = 'app_last_activity';
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos
  private readonly CHECK_INTERVAL = 30 * 1000; // Verificar cada 30 segundos

  public sessionStatus = new BehaviorSubject<boolean>(true);
  public block$ = new Subject<boolean>();

  constructor(
    private keycloak: KeycloakService,
    private userService: UsersService,
    _applicationService: ApplicationsService,
    private translate: TranslateService
  ) {
    this.setupInteractionTracking();
    this.listenToOtherTabs();
  }

  /**
   * Detecta interacción en CUALQUIER pestaña
   */
  private setupInteractionTracking() {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

    events.forEach(event => {
      document.addEventListener(event, () => {
        if (!document.hidden) {
          this.updateLastActivity();
        }
      }, { passive: true });
    });

    // Cuando la pestaña se vuelve visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('Pestaña visible de nuevo');
        // Leer la última actividad de otras pestañas
        this.checkSession();
      }
    });
  }

  /**
   * Escucha cambios de localStorage de otras pestañas
   */
  private listenToOtherTabs() {
    window.addEventListener('storage', (event) => {
      if (event.key === this.LAST_ACTIVITY_KEY && event.newValue) {
        console.log('Actividad detectada en otra pestaña');
        // Otra pestaña tuvo actividad, verificar sesión
        this.checkSession();
      }
    });
  }

  /**
   * Actualiza timestamp de última actividad (compartido entre pestañas)
   */
  private updateLastActivity() {
    const now = Date.now().toString();
    localStorage.setItem(this.LAST_ACTIVITY_KEY, now);
  }

  /**
   * Obtiene la última actividad de CUALQUIER pestaña
   */
  private getLastActivity(): number {
    const stored = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (stored) {
      return parseInt(stored, 10);
    }
    // Si no existe, crear una inicial
    this.updateLastActivity();
    return Date.now();
  }

  public loadUser(): Observable<any> {
    this.startTokenManagement();
    this.subscribeToKeycloakEvents();

    return this.userService.getMyUser().pipe(
      tap((user) => {
        this.currentUser = user;
        const lang = user.language || 'es';

        // Translations are loaded via backend merged endpoint (/api/i18n/:lang.json)
        this.translate.use(lang).subscribe();
      })
    );
  }

  private startTokenManagement() {
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
    }

    // Inicializar última actividad
    this.updateLastActivity();

    // Verificar periódicamente
    this.tokenCheckTimer = setInterval(() => {
      this.checkSession();
    }, this.CHECK_INTERVAL);

    // Primera verificación
    this.checkSession();
  }

  private async checkSession() {
    try {
      const lastActivity = this.getLastActivity();
      const timeSinceLastInteraction = Date.now() - lastActivity;
      const minutesInactive = Math.round(timeSinceLastInteraction / 60000);

      console.log(`⏱️ Inactividad: ${minutesInactive} minutos (límite: ${this.INACTIVITY_TIMEOUT / 60000} min)`);

      // Si ha estado inactivo en TODAS las pestañas
      if (timeSinceLastInteraction > this.INACTIVITY_TIMEOUT) {
        console.log('⏰ Sesión cerrada por inactividad global');
        this.handleSessionEnd('inactivity');
        return;
      }

      // Si hay actividad reciente (en cualquier pestaña), renovar token
      const refreshed = await this.keycloak.updateToken(70);

      if (refreshed) {
        console.log('✅ Token renovado (hay actividad en alguna pestaña)');
      }

    } catch (error) {
      console.error('❌ Error verificando sesión:', error);
      this.handleSessionEnd('error');
    }
  }

  private subscribeToKeycloakEvents() {
    if (this.keycloakEventSubscription) {
      this.keycloakEventSubscription.unsubscribe();
    }

    this.keycloakEventSubscription = this.keycloak.keycloakEvents$.subscribe(
      (event) => {
        console.log('Keycloak event:', event.type);

        switch (event.type) {
          case KeycloakEventType.OnAuthRefreshSuccess:
            console.log('✅ Token renovado por Keycloak');
            break;

          case KeycloakEventType.OnAuthRefreshError:
            console.error('❌ Error renovando token');
            this.handleSessionEnd('refresh_error');
            break;

          case KeycloakEventType.OnTokenExpired:
            console.log('⚠️ Token expirado');
            break;

          case KeycloakEventType.OnAuthLogout:
          case KeycloakEventType.OnAuthError:
            this.handleSessionEnd('keycloak_event');
            break;
        }
      }
    );
  }

  private handleSessionEnd(reason: string) {
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
      this.tokenCheckTimer = null;
    }

    if (!this.sessionStatus.value) {
      return;
    }

    console.log(`🚪 Cerrando sesión por: ${reason}`);

    // Limpiar localStorage
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);

    this.sessionStatus.next(false);
    this.block$.next(true);

    setTimeout(() => {
      this.keycloak.logout(window.location.origin);
    }, 1500);
  }

  public async login(): Promise<void> {
    return this.keycloak.login();
  }

  public logout(): Promise<void> {
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
    }
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
    return this.keycloak.logout(window.location.origin);
  }

  public isRole(role: string): boolean {
    return this.currentUser?.role === role;
  }

  public async isLoggedIn(): Promise<boolean> {
    return this.keycloak.isLoggedIn();
  }

  hasProfile(profile: string): boolean {
    return this.currentUser?.profiles?.split(',').includes(profile) || false;
  }

  public getUsername(): IUser {
    return this.currentUser;
  }

  ngOnDestroy() {
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
    }
    if (this.keycloakEventSubscription) {
      this.keycloakEventSubscription.unsubscribe();
    }
  }
}
